<template>
  <div class="map__container flex">
    <filter-pane
      id="filters-layers"
      title="Select a layer:"
      class="flex-no-shrink"
      :allow-no-selected-dataset="allowNoSelectedDataset"
      :datasets="datasets"
      :footer-text="filterPaneFooterText"
      :header-text="filterPaneHeaderText"
    />
    
    <div class="map__inner-container">
      <v-map
        :search="search"
        :toggle-id="toggleId"
        :modal-id="modalId"
        :allow-no-selected-dataset="allowNoSelectedDataset"
        :mapbox-token="mapboxToken"
        :zoom-to-iso="zoomToIso"
      />
      <div class="map__disclaimer">
        {{ disclaimer }}
      </div>
    </div>

    <layer-legend
      :layer="currentDataset"
      :categorical="isSelectedLayerCategorical"
    />
  </div>
</template>

<script>
import './map.scss'

import axios from 'axios'
import FilterPane from './filters/FilterPane'
import LayerLegend from './layer-legend/LayerLegend'
import VMap from './map/VMap'
import { getLayers } from './helpers/map-helpers'

export default {
  components: {
    FilterPane,
    LayerLegend,
    VMap
  },

  props: {
    search: {
      type: Boolean,
      default: false
    },
    toggleId: {
      type: String,
      default: ''
    },
    modalId: {
      type: String,
      required: true
    },
    allowNoSelectedDataset: {
      type: Boolean,
      default: true
    },
    mapboxToken: {
      type: String,
      required: true
    },
    filterPaneFooterText: {
      type: String,
      default: ''
    },
    filterPaneHeaderText: {
      type: String,
      default: ''
    },
    zoomToIso: {
      type: String,
      default: ''
    },
    disclaimer: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      datasets: [],
      currentDatasetId: ''
    }
  },

  computed: {
    currentDataset () {
      return this.getDatasetFromId(this.currentDatasetId)
    },

    isSelectedLayerCategorical () {
      return this.$store.state.map.isSelectedLayerCategorical
    }
  },

  created () {
    this.$eventHub.$on('reload-all-facets', this.reload)
    this.$eventHub.$on('map-update-curr', this.updateCurrentDataset)
    this.$eventHub.$on('map-load', this.selectFirstLayer)

    this.reload()
  },

  destroyed () {
    this.$eventHub.$off('reload-all-facets', this.reload)
    this.$eventHub.$off('map-update-curr', this.updateCurrentDataset)
    this.$eventHub.$off('map-load', this.selectFirstLayer)
  },

  methods: {
    getDatasetFromId (datasetId) {
      let dataset = null

      this.datasets.forEach(ds => {
        if (ds.id === datasetId) {
          dataset = ds
        }
      })

      return dataset
    },

    getLayerIdsForMap(dataset) {
      const layerIds = []

      if (dataset.map_type === 'Raster') {
        layerIds.push(dataset.id)
      } else {
        for (let i = 0; i < this.layerCount; i++) {
          layerIds.push(dataset.id + '_' + i)
        }
      }

      return layerIds
    },

    reload () {
      axios.get('/layers.json')
        .then(res => {
          this.datasets = res.data
        })
    },

    selectFirstLayer () {
      this.$eventHub.$emit('select-' + this.datasets[0].id)
    },

    updateCurrentDataset ({datasetId, showDataset, createDataset}) {
      this.deselectCurrentDatasetIfNecessary(datasetId, showDataset)
      this.handleDatasetUpdate({datasetId, showDataset, createDataset})

      this.$store.dispatch('map/updateSelectedLayerSlugState', this.currentDataset.slug)
      this.$store.dispatch('map/updateSelectedLayerIdState', this.currentDatasetId)
      this.$store.dispatch('map/updateIsSelectedLayerCategoricalState', this.currentDataset.category === 'Categorical')
    },

    deselectCurrentDatasetIfNecessary (datasetId, showDataset) {
      const isReplacingCurrentDataset = showDataset &&
        this.currentDatasetId &&
        datasetId !== this.currentDatasetId

      if (isReplacingCurrentDataset) {
        this.$eventHub.$emit('deselect-' + this.currentDatasetId)
      }
    },

    handleDatasetUpdate ({datasetId, showDataset, createDataset}) {
      const newDataset = this.getDatasetFromId(datasetId)

      if (createDataset && showDataset) {
        this.$eventHub.$emit('map-create-and-show-layers', getLayers(newDataset, true))
        this.currentDatasetId = datasetId
      } else if (showDataset) {
        this.$eventHub.$emit('map-show-layers', this.getLayerIdsForMap(newDataset))
        this.currentDatasetId = datasetId
      } else {
        this.$eventHub.$emit('map-hide-layers', this.getLayerIdsForMap(newDataset))
        if (this.currentDatasetId === datasetId) { this.currentDatasetId = '' }
      }
    }
  }
}

</script>
